import React, { useState } from "react";
import {
  TextField,
  Button,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff, LockOutlined } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import AuthViewController from "../redux/actions/AuthViewController";
import AuthLayout from "../components/auth/AuthLayout";
import { BRAND, inputSx, primaryButtonSx } from "../components/auth/authTheme";

const ResetPasswordPage = () => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const dispatch = useDispatch();
  const { resetPassword } = AuthViewController();
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const loading = useSelector((state) => state.loading);

  const onSubmit = async (data) => {
    try {
      await dispatch(resetPassword(uid, token, { password: data.password }));
    } catch (error) {
      console.error("Reset failed", error);
    }
  };

  return (
    <AuthLayout
      title="Reset password"
      subtitle="Choose a strong password for your account"
      heroTitle="Secure your account with a new password."
      heroSubtitle="Reset your password quickly and continue managing your HR tasks seamlessly."
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="password"
          control={control}
          rules={{
            required: "Password is required",
            minLength: {
              value: 6,
              message: "Password must be at least 6 characters long",
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label="New Password"
              fullWidth
              type={showPassword ? "text" : "password"}
              error={!!errors.password}
              helperText={errors.password?.message}
              variant="outlined"
              sx={{ ...inputSx, mb: 2.5 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      sx={{ color: BRAND.muted }}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Controller
          name="confirm_password"
          control={control}
          rules={{
            required: "Please confirm your password",
            validate: (value) =>
              value === watch("password") || "Passwords do not match",
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Confirm Password"
              fullWidth
              type={showConfirmPassword ? "text" : "password"}
              error={!!errors.confirm_password}
              helperText={errors.confirm_password?.message}
              variant="outlined"
              sx={{ ...inputSx, mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword((prev) => !prev)
                      }
                      edge="end"
                      sx={{ color: BRAND.muted }}
                    >
                      {showConfirmPassword ? (
                        <Visibility />
                      ) : (
                        <VisibilityOff />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disableElevation
          disabled={loading}
          sx={primaryButtonSx}
        >
          {loading ? "Resetting..." : "Reset Password"}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
